import React, { useEffect, useRef, useState } from "react";
import Container from "../ui/Container";
import { stats } from "../../data/stats";

/** Count from 0 → target once `start` is true (rAF-based, no dependency). */
const useCountUp = (target, start, duration = 1600) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return undefined;
    let frame;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      // easeOutCubic for a natural deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, start, duration]);

  return value;
};

const StatItem = ({ stat, visible }) => {
  const count = useCountUp(stat.to || 0, visible && stat.to != null);
  const display =
    stat.text != null ? stat.text : `${count.toLocaleString()}${stat.suffix || ""}`;

  return (
    <div className="text-center">
      <div className="font-display-lg text-4xl font-bold text-primary-container md:text-5xl">
        {display}
      </div>
      <div className="mt-2 font-label-sm text-label-sm uppercase tracking-wider text-inverse-on-surface/80">
        {stat.label}
      </div>
    </div>
  );
};

const Stats = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-inverse-surface py-16">
      <Container>
        <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-5">
          {stats.map((stat) => (
            <StatItem key={stat.label} stat={stat} visible={visible} />
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Stats;
